'use client';

import Link from 'next/link';
import React, { useRef, useState } from 'react';

import styled from '@emotion/styled';
import { tabsClasses } from '@mui/material';
import MuiButton from '@mui/material/Button';
import { useTranslations } from 'next-intl';
import { useEventListener } from 'usehooks-ts';

import { LanguageMenu } from '@web/features/i18n/LanguageMenu';
import { SignInWallet } from '@web/features/login/SignInWallet';
import { ICompletedUser } from '@web/features/user/service';
import { Avatar, Button, IconButton } from '@web/ui/components';
import { tabsCustomClasses } from '@web/ui/components/MenuSwitcher';
import { LanguageIcon, MenuIcon, PersonaLogo } from '@web/ui/icons';
import { ThemeModeSwitcher } from '@web/ui/theme/ThemeModeSwitcher';
import { IThemeMeta, useDesignsTokenTheme } from '@web/ui/theme/ThemeProvider';
import { getBorder } from '@web/ui/theme/utils';

const HeaderContainer = styled.header<{ isHidden: boolean }>`
  position: sticky;
  top: 0;
  display: grid;
  grid-template-columns: 0fr 1fr auto;
  align-items: center;
  height: 40px;
  padding: ${({ theme }) => theme.spacing('1000')};
  background: ${({ theme }) => theme.color.background};
  z-index: 10;
  transition: transform 300ms ease;

  ${({ isHidden }) =>
    isHidden &&
    `
    transform: translateY(-100%);
  `}

  ${({ theme }) => theme.breakpoints.up('sm')} {
    grid-template-columns: 1fr auto 1fr;
  }
`;

const LeftColumn = styled.div`
  display: none;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    display: flex;
    gap: ${({ theme }) => theme.spacing('1250')};
    align-items: center;
  }
`;

const QuickActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing('0250')};
`;

const SideSheetTrigger = styled(MuiButton)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0500')};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.shape.corner['1250']};
  padding: 0;
  justify-self: flex-end;
  background: transparent !important;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    gap: ${({ theme }) => theme.spacing('1000')};
  }

  :hover span {
    text-decoration: ${({ theme }) => theme.textDecoration.underline};
  }
`;

const SPersonaLogo = styled(PersonaLogo)`
  width: 118px;
  color: ${({ theme }) => theme.color.onBackground};
`;

const UserName = styled.span`
  color: ${({ theme }) => theme.color.onBackground};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['1']};

  display: none;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    display: flex;
  }
`;

const UserAvatar = styled(Avatar)`
  ${getBorder('1', 'onBackground', '0750')};
`;

const MenuContainer = styled.div`
  display: flex;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    display: none;
  }
`;

const RightAction = styled.div`
  justify-self: flex-end;
  display: flex;
  gap: ${({ theme }) => theme.spacing('1000')};
`;

const SThemeModeSwitcher = styled(ThemeModeSwitcher)`
  &.${tabsCustomClasses.root} {
    box-sizing: border-box;
    height: ${({ theme }) => theme.size[2500]};
    background-color: transparent;
    border-radius: ${({ theme }) => theme.shape.corner['0500']};
    border: 1px solid ${({ theme }) => theme.color.outlineVariant};
  }

  .${tabsClasses.indicator} {
    border-radius: ${({ theme }) => theme.spacing('0250')};
    background-color: ${({ theme }) => theme.color.surfaceContainerHighest};
  }
`;

export interface IHeaderProps {
  user: ICompletedUser | undefined;
  enableNavigateBack: boolean;
  className?: string;
  onOpenSideSheet(): void;
  onNavigateBack(): void;
}

export const Header = ({
  user,
  enableNavigateBack,
  className,
  onOpenSideSheet,
  onNavigateBack,
}: IHeaderProps) => {
  const t = useTranslations();
  const { theme, switchTheme } = useDesignsTokenTheme();

  const [isHidden, setIsHidden] = useState(false);
  const documentRef = useRef(window.document);
  const prevScrollPosRef = useRef(0);

  useEventListener(
    'scroll',
    () => {
      const currentScrollPos = window.scrollY;

      const isScrolledDown =
        currentScrollPos > 80 && prevScrollPosRef.current < currentScrollPos;

      setIsHidden(isScrolledDown);
      prevScrollPosRef.current = currentScrollPos;
    },
    documentRef,
    { passive: true },
  );

  const handleChangeMode = (mode: IThemeMeta['mode']) => {
    switchTheme({ mode });
  };

  return (
    <HeaderContainer isHidden={isHidden} className={className}>
      <LeftColumn>
        {enableNavigateBack && (
          <Button
            label={t('HEADER-BACK')}
            variant="secondary"
            background="background"
            color="onBackground"
            onClick={onNavigateBack}
          />
        )}
        <QuickActions>
          <SThemeModeSwitcher
            mode={theme.mode}
            onChange={handleChangeMode}
            compact
          />
          <LanguageMenu
            Trigger={
              <IconButton>
                <LanguageIcon color="onBackground" />
              </IconButton>
            }
          />
        </QuickActions>
      </LeftColumn>
      <Link href="/">
        <SPersonaLogo />
      </Link>
      {user ? (
        <SideSheetTrigger type="button" onClick={onOpenSideSheet} disableRipple>
          <UserName>{user.twitter.displayName}</UserName>
          <MenuContainer>
            <MenuIcon />
          </MenuContainer>
          <UserAvatar
            src={user.twitter.profilePictureURL}
            alt={`${user.twitter.displayName} avatar`}
            size="2500"
            borderRadius="0750"
            tokenId={user.twitter.personaPFPTokenId}
          />
        </SideSheetTrigger>
      ) : (
        <RightAction>
          <SignInWallet />
          <IconButton onClick={onOpenSideSheet}>
            <MenuIcon />
          </IconButton>
        </RightAction>
      )}
    </HeaderContainer>
  );
};
